// ─────────────────────────────────────────────────────────────────
// AiComposer — the "Draft with AI" surface for the post/email editors.
//
// Flow:  compose (prompt + scope + model) → working (cancelable) → result
// (review) → apply to the canvas / regenerate / back to prompt.
//
// Responsive: a floating panel on desktop (canvas stays visible for focus
// feedback), a keyboard-aware bottom sheet on mobile.
//
// Canvas integration:
//  - scope "selected block" = rewrite/focus one canvas object (via ✨ or the
//    current selection); applying REPLACES that block with the draft blocks.
//  - scope "whole post/email" = fresh content; applying APPENDS to the
//    document (or replaces it entirely). Every apply is a single zundo-tracked
//    store action → undoable with ⌘Z, and the first inserted block is scrolled
//    into view + pulsed.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw, Sparkles, Wallet, X } from 'lucide-react';
import type {
  AiDraft,
  AiDraftRequest,
  AiDraftResult,
  AiProvidersResult,
  Block,
  DraftContext,
} from '@blogforge/shared';
import { microCreditsToCredits } from '@blogforge/shared';
import type { DragPath } from 'src/types';
import { api, ApiError } from '../../hooks/api.js';
import { useEditorStore } from '../../stores/editorStore.js';
import { cn, tableSummary } from '../../helpers.js';

// ── Session model preference (per earlier decision: last used per session) ──
const LAST_MODEL_KEY = 'blogforge.ai.lastModel';

interface ModelPick {
  providerId: string;
  modelId: string;
}

function readStoredPick(): ModelPick | null {
  try {
    const raw = window.localStorage.getItem(LAST_MODEL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ModelPick>;
    return parsed.providerId && parsed.modelId
      ? { providerId: parsed.providerId, modelId: parsed.modelId }
      : null;
  } catch {
    return null;
  }
}

function writeStoredPick(pick: ModelPick) {
  try {
    window.localStorage.setItem(LAST_MODEL_KEY, JSON.stringify(pick));
  } catch {
    // Private mode etc. — the preference simply won't persist.
  }
}

export interface AiComposerProps {
  contentKind: 'post' | 'email';
  /** Current title/subject so context + empty-field fill work. */
  meta?: { title?: string; subject?: string };
  /** Called when the draft fills a still-empty title/subject (persists server-side). */
  onMetaCommit?: (patch: { title?: string; subject?: string }) => void;
  onClose: () => void;
}

type Phase =
  | { kind: 'compose' }
  | { kind: 'working' }
  | { kind: 'result'; result: AiDraftResult }
  | {
      kind: 'error';
      message: string;
      /** Set on 402 — drafting was refused for lack of credits. */
      insufficient?: { requiredMicroCredits: number; availableMicroCredits: number };
    };

type Tone = '' | 'professional' | 'friendly' | 'concise';

const TONE_GUIDE: Record<Exclude<Tone, ''>, string> = {
  professional: 'Write in a professional, confident tone.',
  friendly: 'Write in a warm, friendly tone.',
  concise: 'Keep it concise and scannable.',
};

const PROMPT_SUGGESTIONS: Record<'post' | 'email', string[]> = {
  email: [
    'A welcome email for new subscribers',
    'Announce a product launch',
    'A monthly digest of what shipped',
    'A friendly re-engagement email',
  ],
  post: [
    'A step-by-step how-to guide',
    'An opinion piece on a hot topic',
    'A company update / changelog post',
    'A listicle-style post with tips',
  ],
};

const KIND_LABEL = { post: 'post', email: 'email' } as const;

interface SelectionSnapshot {
  id: string;
  path: DragPath;
  block: Block;
  /** True when replacement inside the container is supported (root or sections). */
  supported: boolean;
}

function snapshotSelection(): SelectionSnapshot | null {
  const store = useEditorStore.getState();
  const selected = store.selectedBlockId;
  if (!selected) return null;
  const block = store.getBlockInPath(selected[0], selected[1]);
  if (!block) return null;
  // Section containers are canvas-editable; accordion/columns selections fall
  // back to document scope with an explanation.
  const supported = selected[1].every((seg) => seg.type === 'section');
  return { id: selected[0], path: selected[1], block, supported };
}

function countBlocks(blocks: Block[]): number {
  let n = blocks.length;
  for (const b of blocks) {
    if (b.type === 'section') n += countBlocks((b.attrs.blocks as Block[] | undefined) ?? []);
  }
  return n;
}

/** Short label per block used in the result preview list. */
function blockPreviewLabel(block: Block): string {
  // Not every block carries `content` — a list holds an item template.
  const content = 'content' in block && typeof block.content === 'string' ? block.content.trim() : '';
  if (content) return content.slice(0, 70);
  if (block.type === 'button') return 'Button';
  if (block.type === 'image') return block.attrs.alt || 'Image';
  if (block.type === 'divider') return 'Horizontal divider';
  if (block.type === 'section') {
    const nested = (block.attrs.blocks as Block[] | undefined) ?? [];
    return `Section with ${countBlocks(nested)} nested block${nested.length === 1 ? '' : 's'}`;
  }
  // A table's `content` is only its heading — the shape of the data says more.
  if (block.type === 'table') return `Table — ${tableSummary(block)}`;
  return '';
}

/** Flash a freshly applied block on the canvas so the user sees where it landed. */
function flashAppliedBlock(id: string) {
  window.requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(`[data-block-id="${CSS.escape(id)}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ai-flash');
    window.setTimeout(() => el.classList.remove('ai-flash'), 1600);
  });
}

export function AiComposer({ contentKind, meta, onMetaCommit, onClose }: AiComposerProps) {
  // ── Model catalog ────────────────────────────────────────────
  const {
    data: catalog,
    isLoading: catalogLoading,
    refetch: retryProviders,
  } = useQuery({
    queryKey: ['ai', 'providers'],
    queryFn: api.ai.providers,
    staleTime: 5 * 60 * 1000,
  });

  // ── Local state ──────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>({ kind: 'compose' });
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState<Tone>('');
  const [pick, setPick] = useState<ModelPick | null>(null);
  const [scope, setScope] = useState<'document' | 'selection'>('document');
  const [lastRequest, setLastRequest] = useState<AiDraftRequest | null>(null);

  const hasContent = useEditorStore((s) => s.blocks.length > 0);
  const abortRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();

  // Snapshot the canvas selection once, at open time.
  const selection = useMemo<SelectionSnapshot | null>(snapshotSelection, []);

  // Scope default: a usable canvas selection wins, otherwise the whole document.
  useEffect(() => {
    if (selection?.supported) setScope('selection');
  }, [selection]);

  // Initialize the model pick: stored preference → provider default.
  useEffect(() => {
    if (!catalog || catalog.providers.length === 0) return;
    const stored = readStoredPick();
    const defaultProviderId = catalog.providers.some((p) => p.providerId === stored?.providerId)
      ? (stored?.providerId ?? catalog.defaultProviderId)
      : catalog.defaultProviderId;
    const provider =
      catalog.providers.find((p) => p.providerId === defaultProviderId) ??
      catalog.providers[0] ??
      null;
    if (!provider) return;
    const modelId =
      stored && provider.models.some((m) => m.id === stored.modelId)
        ? stored.modelId
        : provider.defaultModelId;
    setPick({ providerId: provider.providerId, modelId });
  }, [catalog]);

  // Abort in-flight generation when the composer unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Esc closes the composer (never mid-generation — the Cancel button wins).
  useEffect(() => {
    if (phase.kind === 'working') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase.kind, onClose]);

  const selectedProvider = useMemo(() => {
    if (!catalog || !pick) return undefined;
    return catalog.providers.find((p) => p.providerId === pick.providerId);
  }, [catalog, pick]);

  const canGenerate = phase.kind === 'compose' && prompt.trim().length > 0 && pick !== null;

  // ── Request building ─────────────────────────────────────────
  function buildContext(): DraftContext {
    const store = useEditorStore.getState();
    if (scope === 'selection' && selection) {
      return { selection: { blockId: selection.id, block: selection.block } };
    }
    return {
      document: {
        ...(meta?.title?.trim() ? { title: meta.title.trim() } : {}),
        ...(meta?.subject?.trim() ? { subject: meta.subject.trim() } : {}),
        blocks: store.blocks,
      },
    };
  }

  function buildRequest(): AiDraftRequest {
    return {
      kind: contentKind,
      prompt: prompt.trim(),
      ...(tone ? { extraInstructions: TONE_GUIDE[tone] } : {}),
      ...(pick ? { providerId: pick.providerId, modelId: pick.modelId } : {}),
      context: buildContext(),
    };
  }

  // ── Generation ───────────────────────────────────────────────
  async function run(request: AiDraftRequest) {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLastRequest(request);
    setPhase({ kind: 'working' });
    try {
      const next = await api.ai.drafts(request, controller.signal);
      writeStoredPick({ providerId: next.provider, modelId: next.model });
      setPhase({ kind: 'result', result: next });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setPhase({ kind: 'compose' });
        return;
      }
      // A 402 means the active workspace ran out of credits — surface the
      // shortfall so the UI can offer a top-up instead of a plain error.
      const body = err instanceof ApiError ? err.body : undefined;
      const insufficient =
        err instanceof ApiError &&
        err.status === 402 &&
        typeof body?.['availableMicroCredits'] === 'number' &&
        typeof body?.['requiredMicroCredits'] === 'number'
          ? {
              requiredMicroCredits: body['requiredMicroCredits'] as number,
              availableMicroCredits: body['availableMicroCredits'] as number,
            }
          : undefined;
      setPhase({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
        ...(insufficient ? { insufficient } : {}),
      });
    }
  }

  const handleGenerate = () => {
    if (!canGenerate) return;
    void run(buildRequest());
  };

  const handleRegenerate = () => {
    if (lastRequest) void run(lastRequest);
  };

  const handleCancel = () => {
    abortRef.current?.abort(); // run() catches AbortError → back to compose
  };

  const requestClose = () => {
    if (phase.kind !== 'working') onClose();
  };

  // ── Apply to canvas ──────────────────────────────────────────
  function commitEmptyMeta(aiDraft: AiDraft) {
    if (!onMetaCommit) return;
    if (contentKind === 'post' && aiDraft.kind === 'post') {
      const title = aiDraft.title?.trim();
      if (title && !(meta?.title ?? '').trim()) onMetaCommit({ title });
    }
    if (contentKind === 'email' && aiDraft.kind === 'email') {
      const subject = aiDraft.subject?.trim();
      if (subject && !(meta?.subject ?? '').trim()) onMetaCommit({ subject });
    }
  }

  function applyDraft(mode: 'append' | 'replaceAll' | 'replaceSelection') {
    if (phase.kind !== 'result') return;
    const store = useEditorStore.getState();
    if (mode === 'append') store.applyDraftAppend(phase.result.draft.blocks);
    else if (mode === 'replaceAll') store.applyDraftReplaceAll(phase.result.draft.blocks);
    else {
      const ok = store.applyDraftReplaceSelection(phase.result.draft.blocks);
      if (!ok) store.applyDraftAppend(phase.result.draft.blocks); // graceful fallback
    }
    commitEmptyMeta(phase.result.draft);
    const focusId = useEditorStore.getState().selectedBlockId?.[0];
    if (focusId) flashAppliedBlock(focusId);
    onClose();
  }

  const label = KIND_LABEL[contentKind];

  return (
    <div
      className="fixed inset-0 z-[86]"
      role="dialog"
      aria-modal="true"
      aria-label="Draft with AI"
    >
      {/* Mobile-only scrim (desktop keeps the canvas visible) */}
      <div
        className="bg-ink/40 absolute inset-0 backdrop-blur-[2px] lg:hidden"
        onClick={requestClose}
        aria-hidden="true"
      />

      <div className="ai-composer-panel border-line bg-card absolute inset-x-0 bottom-0 flex max-h-[88dvh] flex-col overflow-hidden border-t pb-[env(safe-area-inset-bottom)] shadow-[0_-28px_70px_-32px_rgba(2,6,18,0.55)] sm:mx-auto sm:w-[min(100vw-2rem,30rem)] sm:rounded-t-2xl sm:border lg:inset-x-auto lg:inset-y-6 lg:right-4 lg:mx-0 lg:max-h-none lg:w-[400px] lg:rounded-2xl lg:pb-0 lg:shadow-[0_34px_90px_-28px_rgba(2,6,18,0.6)]">
        {/* Grabber (mobile) */}
        <div className="flex justify-center pt-2 lg:hidden">
          <div className="bg-line-strong h-1 w-10 rounded-full" />
        </div>

        {/* ── Header ───────────────────────────────────────── */}
        <div className="flex shrink-0 items-start gap-2 px-4 pt-3 pb-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Sparkles size={15} className="text-accent" />
              <h2 className="text-ink truncate text-[15px] font-bold tracking-tight">
                Draft with AI
              </h2>
              <span className="bg-card-2 text-muted rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
                {label}
              </span>
            </div>
            <p className="text-muted mt-0.5 truncate text-[11px]">
              Describe what you want — I’ll return blocks that match your editor.
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={phase.kind === 'working'}
            title="Close (Esc)"
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1 transition-colors disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Scope + model row ────────────────────────────── */}
        <div className="border-line flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2">
          <ScopeSegmented
            contentKind={contentKind}
            scope={scope}
            selection={selection}
            onChange={setScope}
          />
          <div className="ml-auto flex items-center gap-2">
            {selectedProvider?.mode === 'stub' && (
              <span className="bg-warn/10 text-warn hidden rounded-full px-2 py-0.5 text-[10px] font-semibold sm:inline">
                demo stub
              </span>
            )}
            <ModelSelect catalog={catalog} pick={pick} onChange={setPick} />
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────── */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {catalogLoading && (
            <div className="space-y-3 p-4">
              <div className="bg-card-2 h-4 w-3/4 animate-pulse rounded" />
              <div className="bg-card-2 h-4 w-1/2 animate-pulse rounded" />
              <div className="bg-card-2 h-24 w-full animate-pulse rounded-lg" />
            </div>
          )}

          {!catalogLoading && !catalog && (
            <ErrorState
              message="Couldn’t load the AI providers — is the server running?"
              onRetry={() => void retryProviders()}
            />
          )}

          {catalog && phase.kind === 'compose' && (
            <ComposeView
              contentKind={contentKind}
              scope={scope}
              selection={selection}
              prompt={prompt}
              tone={tone}
              canGenerate={canGenerate}
              onPromptChange={setPrompt}
              onToneChange={setTone}
              onGenerate={handleGenerate}
            />
          )}

          {phase.kind === 'working' && (
            <WorkingView
              label={label}
              modelLabel={pick ? `${providerLabel(catalog, pick)} · ${pick.modelId}` : ''}
              onCancel={handleCancel}
            />
          )}

          {phase.kind === 'error' && !phase.insufficient && (
            <ErrorState
              message={phase.message}
              onRetry={lastRequest ? handleRegenerate : handleGenerate}
              onBack={() => setPhase({ kind: 'compose' })}
            />
          )}

          {phase.kind === 'error' && phase.insufficient && (
            <div className="flex h-full min-h-56 flex-col items-center justify-center gap-3 p-6 text-center">
              <div className="bg-danger/10 text-danger flex h-12 w-12 items-center justify-center rounded-2xl">
                <Wallet size={22} strokeWidth={1.9} />
              </div>
              <div>
                <h3 className="text-[14px] font-semibold text-ink">Not enough credits</h3>
                <p className="text-muted mx-auto mt-1 max-w-[260px] text-[12px] leading-relaxed">
                  This draft needs{' '}
                  {microCreditsToCredits(
                    phase.insufficient.requiredMicroCredits,
                  ).toLocaleString(undefined, { maximumFractionDigits: 4 })}{' '}
                  credits, but only{' '}
                  {microCreditsToCredits(
                    phase.insufficient.availableMicroCredits,
                  ).toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
                  remain on this workspace. Top up to keep drafting.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/billing')}
                className="btn-primary px-4 py-2 text-xs"
              >
                Top up credits
              </button>
              <button
                type="button"
                onClick={() => setPhase({ kind: 'compose' })}
                className="text-muted hover:text-ink-soft text-[11px] font-medium"
              >
                Go back to the prompt
              </button>
            </div>
          )}

          {phase.kind === 'result' && (
            <ResultView
              result={phase.result}
              modelLabel={modelLabelFor(phase.result.model, catalog)}
              scope={scope}
              hasContent={hasContent}
              selectionUsable={selection?.supported === true}
              onApplyReplaceSelection={() => applyDraft('replaceSelection')}
              onApplyAppend={() => applyDraft('append')}
              onApplyReplaceAll={() => applyDraft('replaceAll')}
              onRegenerate={handleRegenerate}
              onBackToPrompt={() => setPhase({ kind: 'compose' })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Sub-views
// ─────────────────────────────────────────────────────────────────

function providerLabel(catalog: AiProvidersResult | undefined, pick: ModelPick): string {
  const provider = catalog?.providers.find((p) => p.providerId === pick.providerId);
  return provider?.label ?? pick.providerId;
}

function modelLabelFor(modelId: string, catalog: AiProvidersResult | undefined): string {
  for (const provider of catalog?.providers ?? []) {
    const model = provider.models.find((m) => m.id === modelId);
    if (model) return model.label;
  }
  return modelId;
}

interface ComposeViewProps {
  contentKind: 'post' | 'email';
  scope: 'document' | 'selection';
  selection: SelectionSnapshot | null;
  prompt: string;
  tone: Tone;
  canGenerate: boolean;
  onPromptChange: (value: string) => void;
  onToneChange: (value: Tone) => void;
  onGenerate: () => void;
}

function ComposeView({
  contentKind,
  scope,
  selection,
  prompt,
  tone,
  canGenerate,
  onPromptChange,
  onToneChange,
  onGenerate,
}: ComposeViewProps) {
  const scopeHint =
    scope === 'selection' && selection ? (
      <p className="text-ink-soft flex items-center gap-1.5 text-[11px]">
        <span className="bg-accent/10 text-accent rounded px-1.5 py-0.5 font-mono text-[10px]">
          {selection.block.type}
        </span>
        Focusing on the selected block — the result replaces it.
      </p>
    ) : selection && !selection.supported ? (
      <p className="text-warn text-[11px]">
        The selected block lives in a container that can’t be replaced yet — drafting the whole{' '}
        {contentKind} instead.
      </p>
    ) : (
      <p className="text-muted text-[11px]">
        Fresh content for your {contentKind} — it will be added to the end of the canvas.
      </p>
    );

  return (
    <div className="space-y-3 p-4">
      {scopeHint}

      {/* Prompt suggestions */}
      {prompt.trim().length === 0 && (
        <div className="flex flex-wrap gap-1.5">
          {PROMPT_SUGGESTIONS[contentKind].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onPromptChange(s)}
              className="border-line bg-card text-muted hover:border-accent-border hover:text-accent rounded-full border px-2.5 py-1 text-[11px] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <textarea
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') onGenerate();
        }}
        placeholder={
          contentKind === 'email'
            ? 'e.g. A welcome email for new subscribers of a dev-tools newsletter…'
            : 'e.g. A how-to guide about shipping a self-hosted app…'
        }
        rows={5}
        autoFocus
        className="field-input w-full resize-none py-2.5 text-sm leading-relaxed"
      />

      <div className="flex items-center justify-between gap-3">
        <label className="text-muted flex items-center gap-2 text-[11px]">
          <span>Mood</span>
          <select
            value={tone}
            onChange={(e) => onToneChange(e.target.value as Tone)}
            className="field-input h-7 rounded-md px-1.5 text-[11px]"
          >
            <option value="">Natural</option>
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="concise">Concise</option>
          </select>
        </label>
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate}
          className={cn(
            'btn-primary shrink-0 rounded-lg px-3.5 py-2 text-xs',
            !canGenerate && 'opacity-40',
          )}
          title="Generate draft (⌘↵)"
        >
          <Sparkles size={13} />
          Generate draft
        </button>
      </div>
    </div>
  );
}

function WorkingView({
  label,
  modelLabel,
  onCancel,
}: {
  label: string;
  modelLabel: string;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4 px-4 py-8">
      <div className="bg-accent/10 flex h-10 w-10 items-center justify-center rounded-full">
        <Loader2 size={18} className="text-accent animate-spin" />
      </div>
      <p className="text-ink-soft text-center text-[13px]">
        Building your {label}…
        <br />
        <span className="text-muted text-[11px]">{modelLabel}</span>
      </p>
      {/* Skeleton preview so the waiting state reads as a real layout */}
      <div className="w-full max-w-xs space-y-2">
        <div className="bg-card-2 h-3.5 w-2/3 animate-pulse rounded" />
        <div className="bg-card-2 h-3.5 w-full animate-pulse rounded" />
        <div className="bg-card-2 h-3.5 w-5/6 animate-pulse rounded" />
        <div className="bg-card-2 h-20 w-full animate-pulse rounded-lg" />
      </div>
      <button type="button" onClick={onCancel} className="btn-ghost rounded-lg px-3 py-1.5 text-xs">
        Cancel
      </button>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
  onBack,
}: {
  message: string;
  onRetry: () => void;
  onBack?: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      <p className="text-ink-soft text-[13px]">{message}</p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRetry}
          className="btn-primary rounded-lg px-3 py-1.5 text-xs"
        >
          <RefreshCw size={12} />
          Try again
        </button>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="btn-ghost rounded-lg px-3 py-1.5 text-xs"
          >
            Back to prompt
          </button>
        )}
      </div>
    </div>
  );
}

interface ScopeSegmentedProps {
  contentKind: 'post' | 'email';
  scope: 'document' | 'selection';
  selection: SelectionSnapshot | null;
  onChange: (scope: 'document' | 'selection') => void;
}

function ScopeSegmented({ contentKind, scope, selection, onChange }: ScopeSegmentedProps) {
  const blockDisabled = !selection?.supported;
  const seg = (active: boolean) =>
    cn(
      'rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
      active ? 'bg-card text-ink shadow-sm' : 'text-muted hover:text-ink-soft',
    );
  return (
    <div className="bg-card-2 flex items-center gap-0.5 rounded-lg p-0.5">
      <button
        type="button"
        onClick={() => onChange('document')}
        className={seg(scope === 'document')}
      >
        Whole {contentKind}
      </button>
      <button
        type="button"
        disabled={blockDisabled}
        onClick={() => onChange('selection')}
        title={blockDisabled ? 'Select a block on the canvas first' : undefined}
        className={cn(seg(scope === 'selection'), blockDisabled && 'cursor-not-allowed opacity-40')}
      >
        Selected block
      </button>
    </div>
  );
}

interface ModelSelectProps {
  catalog: AiProvidersResult | undefined;
  pick: ModelPick | null;
  onChange: (pick: ModelPick) => void;
}

function ModelSelect({ catalog, pick, onChange }: ModelSelectProps) {
  if (!catalog || catalog.providers.length === 0 || !pick) {
    return <div className="bg-card-2 h-7 w-24 animate-pulse rounded" />;
  }

  const handleChange = (modelId: string) => {
    for (const provider of catalog.providers) {
      if (provider.models.some((m) => m.id === modelId)) {
        onChange({ providerId: provider.providerId, modelId });
        return;
      }
    }
  };

  return (
    <label className="text-muted flex items-center gap-1.5 text-[11px]">
      <span className="hidden sm:inline">Model</span>
      <select
        value={pick.modelId}
        onChange={(e) => handleChange(e.target.value)}
        className="field-input h-7 max-w-[13rem] rounded-md px-1.5 text-[11px]"
        title="Model used for this draft"
      >
        {catalog.providers.map((provider) => (
          <optgroup key={provider.providerId} label={provider.label}>
            {provider.models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

interface ResultViewProps {
  result: AiDraftResult;
  modelLabel: string;
  scope: 'document' | 'selection';
  hasContent: boolean;
  selectionUsable: boolean;
  onApplyReplaceSelection: () => void;
  onApplyAppend: () => void;
  onApplyReplaceAll: () => void;
  onRegenerate: () => void;
  onBackToPrompt: () => void;
}

function ResultView({
  result,
  modelLabel,
  scope,
  hasContent,
  selectionUsable,
  onApplyReplaceSelection,
  onApplyAppend,
  onApplyReplaceAll,
  onRegenerate,
  onBackToPrompt,
}: ResultViewProps) {
  const { draft } = result;

  // Preview rows: headline + up to 6 blocks (sections unfold one level).
  const { rows, total } = useMemo(() => {
    const list: { type: string; label: string }[] = [];
    const push = (b: Block) => list.push({ type: b.type, label: blockPreviewLabel(b) });
    for (const b of draft.blocks) {
      if (list.length >= 6) break;
      push(b);
      if (b.type === 'section') {
        for (const inner of (b.attrs.blocks as Block[] | undefined) ?? []) {
          if (list.length >= 6) break;
          push(inner);
        }
      }
    }
    return { rows: list, total: countBlocks(draft.blocks) };
  }, [draft]);

  const replaceSelection = scope === 'selection' && selectionUsable;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {/* Headline card */}
        <div className="border-line bg-card-2/50 rounded-xl border p-3">
          {draft.kind === 'email' ? (
            <>
              <p className="text-ink text-[13px] font-semibold">{draft.subject}</p>
              {draft.previewText && (
                <p className="text-muted mt-1 text-[11px] italic">{draft.previewText}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-ink text-[13px] font-semibold">{draft.title}</p>
              {draft.excerpt && (
                <p className="text-muted mt-1 text-[11px] italic">{draft.excerpt}</p>
              )}
            </>
          )}
          <p className="text-muted mt-2 text-[10px] tracking-wide uppercase">
            {result.provider} · {modelLabel} · {total} block{total === 1 ? '' : 's'}
          </p>
          {typeof result.creditsChargedMicro === 'number' && (
            <p className="text-muted mt-1 flex items-center gap-1 text-[10.5px]">
              <Wallet size={10} />
              Used{' '}
              {microCreditsToCredits(result.creditsChargedMicro).toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}{' '}
              credits
              {typeof result.creditsRemainingMicro === 'number' &&
                ` · ${microCreditsToCredits(result.creditsRemainingMicro).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })} remaining`}
            </p>
          )}
        </div>

        {/* Block preview list */}
        <div className="space-y-1">
          {rows.map((row, i) => (
            <div key={`${row.type}-${i}`} className="flex items-center gap-2 rounded-md px-1 py-1">
              <span className="bg-card-2 text-muted w-14 flex-none truncate rounded px-1.5 py-0.5 text-center text-[9px] font-semibold tracking-wide uppercase">
                {row.type}
              </span>
              <span className="text-ink-soft truncate text-[11px]">{row.label || '(empty)'}</span>
            </div>
          ))}
          {rows.length < total && (
            <p className="text-muted px-1 pt-1 text-[10px]">… more content in the draft</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="border-line shrink-0 space-y-2 border-t p-3">
        {!replaceSelection && hasContent && (
          <button
            type="button"
            onClick={onApplyReplaceAll}
            className="border-line text-muted hover:border-danger/30 hover:text-danger w-full rounded-lg border px-3 py-1.5 text-[11px] transition-colors"
          >
            Replace the whole draft with this instead
          </button>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={replaceSelection ? onApplyReplaceSelection : onApplyAppend}
            className="btn-primary flex-1 rounded-lg px-3 py-2 text-xs"
          >
            <Sparkles size={13} />
            {replaceSelection ? 'Replace selection' : 'Add to my draft'}
          </button>
          {replaceSelection && (
            <button
              type="button"
              onClick={onApplyAppend}
              className="btn-ghost rounded-lg px-3 py-2 text-xs"
            >
              Append instead
            </button>
          )}
        </div>
        <div className="text-muted flex items-center justify-center gap-4 text-[11px]">
          <button
            type="button"
            onClick={onRegenerate}
            className="hover:text-ink-soft inline-flex items-center gap-1 transition-colors"
          >
            <RefreshCw size={11} />
            Regenerate
          </button>
          <span className="text-line-strong">·</span>
          <button
            type="button"
            onClick={onBackToPrompt}
            className="hover:text-ink-soft transition-colors"
          >
            Edit prompt
          </button>
        </div>
      </div>
    </div>
  );
}
